import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ECharts } from 'echarts';
import { useTranslation } from 'react-i18next';
import { AnomalyMetricKey, AnomalyMetricSemantics } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import { AnomalyMarkerTooltip } from '../anomaly-marker-tooltip/AnomalyMarkerTooltip';
import { GroupedAnomalyMarker } from '../../groupedAnomalyMarkers';
import { resolveMarkerVisualState } from '../../resolveMarkerVisualState';
import AnomalyAxisMarkerIcon from './AnomalyAxisMarkerIcon';

export type { GroupedAnomalyMarker } from '../../groupedAnomalyMarkers';
export { groupAnomaliesByChartDate } from '../../groupedAnomalyMarkers';

const HOVER_CLOSE_DELAY_MS = 150;
const POPOVER_GAP_PX = 8;

type MarkerPosition = {
  dateKey: string;
  left: number;
  top: number;
};

interface AnomalyAxisMarkersProps {
  chart: ECharts | null;
  markers: GroupedAnomalyMarker[];
  metricSemantics: Record<AnomalyMetricKey, AnomalyMetricSemantics>;
  dateTooltipFormatter: (raw: string) => string;
  /** Y value that maps to the bottom edge of the chart grid (x-axis line). */
  xAxisBottomValue: number;
  /** Y axis used to anchor markers on the x-axis (must stay visible regardless of legend toggles). */
  yAxisIndex?: number;
}

const MARKER_TOP_OFFSET_PX = 8;

const computeMarkerPositions = (
  chart: ECharts,
  dateKeys: string[],
  xAxisBottomValue: number,
  yAxisIndex: number,
): MarkerPosition[] => {
  if (chart.isDisposed()) {
    return [];
  }

  return dateKeys.flatMap((dateKey) => {
    try {
      const pixel = chart.convertToPixel(
        { xAxisIndex: 0, yAxisIndex },
        [dateKey, xAxisBottomValue],
      );
      if (!pixel || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) {
        return [];
      }

      return [{ dateKey, left: pixel[0], top: pixel[1] - MARKER_TOP_OFFSET_PX }];
    } catch {
      return [];
    }
  });
};

const AnomalyAxisMarkers: React.FC<AnomalyAxisMarkersProps> = ({
  chart,
  markers,
  metricSemantics,
  dateTooltipFormatter,
  xAxisBottomValue,
  yAxisIndex = 0,
}) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<MarkerPosition[]>([]);
  const [pinnedDateKey, setPinnedDateKey] = useState<string | null>(null);
  const [hoverDateKey, setHoverDateKey] = useState<string | null>(null);

  const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const popoverRef = useRef<HTMLDivElement>(null);

  const visibleDateKey = hoverDateKey ?? pinnedDateKey;

  const cancelHoverClose = useCallback((): void => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback((): void => {
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoverDateKey(null);
      hoverCloseTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelHoverClose]);

  const dismissAll = useCallback((): void => {
    cancelHoverClose();
    setPinnedDateKey(null);
    setHoverDateKey(null);
  }, [cancelHoverClose]);

  const handleIconMouseEnter = useCallback(
    (dateKey: string): void => {
      cancelHoverClose();
      setPinnedDateKey((pinned) => (pinned && pinned !== dateKey ? null : pinned));
      setHoverDateKey(dateKey);
    },
    [cancelHoverClose],
  );

  const handleIconMouseLeave = useCallback((): void => {
    scheduleHoverClose();
  }, [scheduleHoverClose]);

  const handleIconClick = useCallback(
    (dateKey: string, event: React.MouseEvent): void => {
      event.stopPropagation();
      cancelHoverClose();
      setPinnedDateKey((pinned) => (pinned === dateKey ? null : dateKey));
      setHoverDateKey(dateKey);
    },
    [cancelHoverClose],
  );

  const setTriggerRef = useCallback((dateKey: string, element: HTMLButtonElement | null): void => {
    if (element) {
      triggerRefs.current.set(dateKey, element);
      return;
    }
    triggerRefs.current.delete(dateKey);
  }, []);

  const updatePositions = useCallback(() => {
    if (!chart || markers.length === 0) {
      if (!chart) {
        setPositions([]);
      }
      return;
    }

    if (chart.isDisposed()) {
      return;
    }

    const dateKeys = markers.map((marker) => marker.dateKey);
    setPositions(computeMarkerPositions(chart, dateKeys, xAxisBottomValue, yAxisIndex));
  }, [chart, markers, xAxisBottomValue, yAxisIndex]);

  useEffect(() => {
    updatePositions();
    if (!chart || chart.isDisposed()) {
      return;
    }

    const handleLayoutChange = (): void => {
      updatePositions();
    };

    chart.on('finished', handleLayoutChange);
    window.addEventListener('resize', handleLayoutChange);

    return () => {
      if (!chart.isDisposed()) {
        chart.off('finished', handleLayoutChange);
      }
      window.removeEventListener('resize', handleLayoutChange);
    };
  }, [chart, updatePositions]);

  useEffect(() => {
    dismissAll();
  }, [markers, dismissAll]);

  useEffect(() => {
    if (!visibleDateKey) {
      return;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;

      if (popoverRef.current?.contains(target)) {
        return;
      }

      const clickedTrigger = Array.from(triggerRefs.current.values()).some((trigger) =>
        trigger.contains(target),
      );
      if (clickedTrigger) {
        return;
      }

      dismissAll();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [visibleDateKey, dismissAll]);

  useEffect(() => {
    return () => cancelHoverClose();
  }, [cancelHoverClose]);

  if (!chart || markers.length === 0 || positions.length === 0) {
    return null;
  }

  const markersByDate = new Map(markers.map((marker) => [marker.dateKey, marker]));
  const visibleMarker = visibleDateKey ? markersByDate.get(visibleDateKey) : undefined;
  const visiblePosition = visibleDateKey
    ? positions.find((position) => position.dateKey === visibleDateKey)
    : undefined;
  const ariaLabel = t('anomalyTooltipTitle');

  return (
    <div className="anomaly-axis-markers" aria-hidden={false}>
      {positions.map((position) => {
        const marker = markersByDate.get(position.dateKey);
        if (!marker || !(marker.averagePosition ?? marker.brandScore ?? marker.visibility)) {
          return null;
        }

        const isPinned = pinnedDateKey === position.dateKey;
        const isVisible = visibleDateKey === position.dateKey;
        const visualState = resolveMarkerVisualState(marker);
        if (!visualState) {
          return null;
        }

        return (
          <button
            key={position.dateKey}
            ref={(element) => setTriggerRef(position.dateKey, element)}
            type="button"
            className={`anomaly-axis-marker anomaly-axis-marker--${visualState.mode}${isPinned ? ' anomaly-axis-marker--open' : ''}`}
            style={{ left: `${position.left}px`, top: `${position.top}px` }}
            onMouseEnter={() => handleIconMouseEnter(position.dateKey)}
            onMouseLeave={handleIconMouseLeave}
            onClick={(event) => handleIconClick(position.dateKey, event)}
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={isVisible}
          >
            <AnomalyAxisMarkerIcon visualState={visualState} />
          </button>
        );
      })}

      {visibleDateKey && visiblePosition && visibleMarker && (
        <div
          ref={popoverRef}
          className="anomaly-axis-marker__popover"
          role="dialog"
          aria-label={ariaLabel}
          style={{
            top: visiblePosition.top - POPOVER_GAP_PX,
            left: visiblePosition.left,
            transform: 'translate(-50%, -100%)',
          }}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={scheduleHoverClose}
        >
          <AnomalyMarkerTooltip
            marker={visibleMarker}
            metricSemantics={metricSemantics}
            dateFormatter={dateTooltipFormatter}
          />
        </div>
      )}
    </div>
  );
};

export default AnomalyAxisMarkers;
